import React, { useEffect, useState } from "react";
import { Button, DatePicker, Empty, Input, Spin } from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  SaveOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";

// import components
import openNotification from "../../components/Notifications/openNotification";
import ReportItem from "../../components/DailyReport/ReportItem";

// import utils
import getDate from "../../untils/getDate";
import isEmpty from "../../validations/is-empty";
import apiClient from "../../actions/api";

// import styles
import "../../style/auth/dailyupdates.scss";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const DailyUpdates = ({ title }) => {
  document.title = title;

  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const authUser = useSelector((state) => state.auth.user);
  const isAdmin = Number(authUser?.role) === 1;

  const { TextArea } = Input;
  const dateFormat = "YYYY-MM-DD";
  const customWeekStartEndFormat = (value) =>
    `${dayjs(value)
      .tz(timezoneMode.timezoneMode, true)
      .startOf("week")
      .format(dateFormat)} ~ ${dayjs(value)
      .tz(timezoneMode.timezoneMode, true)
      .endOf("week")
      .format(dateFormat)}`;

  const [loading, setLoading] = useState(false);
  const [dailyReportList, setDailyReportList] = useState({});
  const [reportDate, setReportDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [dailyText, setDailyText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editReportId, setEditReportId] = useState(null);
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true).startOf("week"),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true).endOf("week"),
  );

  const handleEditReport = (data) => {
    setEditMode(true);
    setEditReportId(data._id);
    setReportDate(
      dayjs(dayjs(getDate(data.report_date), dateFormat)).tz(
        timezoneMode.timezoneMode,
        true,
      ),
    );
    setDailyText(data.content);
  };

  const handleCancelEditMode = () => {
    setEditMode(false);
    setEditReportId(null);
    setReportDate(dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true));
    setDailyText("");
  };

  const handleDeleteReport = async (reportId) => {
    if (isEmpty(reportId)) {
      openNotification(
        "error",
        "Delete Error",
        "No report selected for delete.",
      );
      return;
    }

    setLoading(true);

    await apiClient
      .post("/reports/delete", { reportID: reportId })
      .then(async (res) => {
        if (res.data?.status === "success") {
          openNotification("success", "Success", res.data.msg);
          await getReports();
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  const handleSaveReport = async () => {
    if (isEmpty(dailyText)) {
      openNotification(
        "error",
        "Validation Error",
        "Please type what you have done today.",
      );
      return;
    }

    if (isEmpty(authUser) || isEmpty(authUser._id)) {
      openNotification("error", "User Error", "User information is missing.");
      return;
    }

    if (editMode && isEmpty(editReportId)) {
      openNotification("error", "Edit Error", "No report selected for edit.");
      return;
    }

    const params = {
      user: authUser._id,
      report_date: dayjs(reportDate).tz(timezoneMode.timezoneMode),
      content: dailyText.trim(),
    };

    const editParams = {
      reportID: editReportId,
      user: authUser._id,
      report_date: dayjs(reportDate).tz(timezoneMode.timezoneMode),
      content: dailyText.trim(),
    };

    setLoading(true);

    await apiClient
      .post(
        editMode ? "/reports/update" : "/reports/add",
        editMode ? editParams : params,
      )
      .then(async (res) => {
        if (res.data?.status === "success") {
          openNotification("success", "Success", res.data.msg);
          await getReports();
        }
        setEditMode(false);
        setEditReportId(null);
        setReportDate(
          dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
        );
        setDailyText("");
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  const getReports = async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      searchStartDate: timeStartInGMTPlus9,
      searchEndDate: timeEndInGMTPlus9,
    };

    setLoading(true);

    await apiClient
      .post("/reports/get", params)
      .then((res) => {
        if (res.data?.status === "success") {
          const tempData = [...(res.data?.dailyReportList || [])];
          if (res.data?.groupedForAdmin) {
            tempData.sort((a, b) => {
              const groupA = a.group?.name || "Ungrouped users";
              const groupB = b.group?.name || "Ungrouped users";
              return (
                groupA.localeCompare(groupB) ||
                new Date(b.report_date) - new Date(a.report_date)
              );
            });
          }

          const grouped = tempData.reduce((acc, report) => {
            const date = getDate(report.report_date);
            const groupName = report.group?.name || "Ungrouped users";
            const groupId = report.group?._id || "ungrouped";
            const bucketKey = res.data?.groupedForAdmin
              ? `${groupId}|||${groupName}|||${date}`
              : date;
            if (!acc[bucketKey]) {
              acc[bucketKey] = [];
            }
            acc[bucketKey].push(report);
            return acc;
          }, {});

          setDailyReportList(grouped);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    getReports();
  }, []);

  return (
    <div
      className={`dailyUpdatesSection ${
        themeMode.themeMode
          ? "dailyUpdatesSection--dark"
          : "dailyUpdatesSection--light"
      }`}
    >
      <header className="daily-page-header">
        <div className="daily-page-heading">
          <span className="daily-page-icon">
            <FileTextOutlined />
          </span>
          <div>
            <h2>Daily Reports</h2>
            <p>Share progress, review team activity, and keep work visible.</p>
          </div>
        </div>
        <div className="daily-report-count">
          <span>
            {Object.values(dailyReportList).reduce(
              (total, reports) => total + reports.length,
              0,
            )}
          </span>
          reports this week
        </div>
      </header>
      <div className="dailyUpdatesWrapper">
        {loading && (
          <div className="dailyUpdateLoading">
            <div className="daily-loading-indicator">
              <Spin size="large" />
              <span>Updating reports...</span>
            </div>
          </div>
        )}

        <div className="dailyUpdatesSearchSection">
          <div className="daily-panel-header">
            <div>
              <h3>{editMode ? "Edit report" : "New report"}</h3>
              <p>
                {editMode
                  ? "Update your selected daily entry."
                  : "Record what you accomplished today."}
              </p>
            </div>
            <span
              className={`daily-mode-badge ${editMode ? "editing" : "creating"}`}
            >
              {editMode ? "Editing" : "New entry"}
            </span>
          </div>
          <div className="search-content daily-compose-form">
            <div className="search-item">
              <label>Report date</label>
              <DatePicker
                format={dateFormat}
                placeholder="Report Date"
                value={reportDate}
                allowClear={false}
                onChange={(date, dateString) => {
                  setReportDate(
                    dayjs(dateString).tz(timezoneMode.timezoneMode, true),
                  );
                }}
              />
            </div>
            <div className="search-item">
              <label>Daily update</label>
              <TextArea
                rows={10}
                placeholder="Summarize completed work, progress, blockers, and next steps..."
                value={dailyText}
                onChange={(e) => setDailyText(e.target.value)}
                className="update-textarea"
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div className="daily-compose-footer">
            <span>{dailyText.length.toLocaleString()} characters</span>
            <div className="daily-compose-actions">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => {
                  handleSaveReport();
                }}
                loading={loading}
              >
                {editMode ? "Update Report" : "Save Report"}
              </Button>
              {editMode && (
                <Button
                  type="default"
                  onClick={() => {
                    handleCancelEditMode();
                  }}
                  loading={loading}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="dailyUpdatesResultSection">
          <div className="daily-results-toolbar">
            <div>
              <h3>Team activity</h3>
              <p>Daily updates grouped by reporting date.</p>
            </div>
            <div className="daily-week-filter">
              <CalendarOutlined />
              <DatePicker
                onChange={(date, dateString) => {
                  const selectedDate = isEmpty(dateString)
                    ? dayjs(getDate(dayjs())).tz(
                        timezoneMode.timezoneMode,
                        true,
                      )
                    : dayjs(date).tz(timezoneMode.timezoneMode, true);
                  setSearchStartDate(selectedDate.startOf("week"));
                  setSearchEndDate(selectedDate.endOf("week"));
                }}
                value={dayjs(searchStartDate)
                  .tz(timezoneMode.timezoneMode, true)
                  .startOf("week")}
                format={customWeekStartEndFormat}
                picker="week"
                disabled={loading}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={getReports}
                loading={loading}
              >
                Search
              </Button>
            </div>
          </div>
          {Object.keys(dailyReportList).length === 0 ? (
            <div className="empty-dailyUpdatesResult">
              <Empty />
            </div>
          ) : (
            Object.entries(dailyReportList).map(([bucketKey, reports]) => {
              const keyParts = bucketKey.split("|||");
              const date = keyParts[keyParts.length - 1];
              const groupName = keyParts.length > 1 ? keyParts[1] : null;
              return (
                <div key={bucketKey} className="result-item">
                  <div className="result-header">
                    <div className="result-count">
                      {isAdmin && groupName && (
                        <span className="daily-report-group-name">
                          <TeamOutlined /> {groupName}
                        </span>
                      )}
                      <span className="result-count-num">{date}</span>
                      <span className="result-count-label">
                        {reports.length}{" "}
                        {reports.length === 1 ? "update" : "updates"}
                      </span>
                    </div>
                  </div>
                  <div className="result-content">
                    {reports.map((report) => (
                      <ReportItem
                        key={report._id}
                        data={report}
                        handleEditReport={handleEditReport}
                        handleDeleteReport={handleDeleteReport}
                        userId={authUser._id}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyUpdates;
